import { useContext, createContext, useState } from 'react';

import { services } from '@widget/config/redirects';

import type { FC, ReactNode } from 'react';

export type AgentInfo = {
  name: string;
  imageUrl: string;
  zipcode: string;
  city: string;
};

export type AgentContextValue = {
  agent: AgentInfo | null;
  isLoading: boolean;
  loadAgentInfo: (zipCode: string) => void;
};

type Props = {
  children: ReactNode;
  widgetXBaseApi?: string;
};

export const AgentContext = createContext<AgentContextValue | null>(null);

export const useAgentContext = (): AgentContextValue => {
  const context = useContext(AgentContext);

  if (!context) throw new Error('useAgentContext is used outside of AgentContext.Provider');

  return context;
};

export const AgentProvider: FC<Props> = ({ children }) => {
  const [agent, setAgent] = useState<AgentInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadAgentInfo = async (zipCode: string) => {
    setIsLoading(true);

    try {
      const agentRequest = await fetch(
        `${services.widgetApp}/api/agent-by-zipcode?zipcode=${zipCode || ''}`,
      );
      const response = await agentRequest.json();
      setAgent(response.agent as AgentInfo);
    } catch {
      setAgent(null);
    }

    setIsLoading(false);
  };

  return (
    <AgentContext.Provider
      value={{
        isLoading,
        agent,
        loadAgentInfo,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};
